import { useRef } from "react";

import {
  HiddenSelect,
  useButton,
  useSelect,
  useListBox,
  useOption,
  type AriaListBoxOptions,
  type AriaSelectProps,
} from "react-aria";
import { useSelectState, type Node, type SelectState } from "react-stately";

import { CaretRightIcon } from "#icons";
import { cn } from "#utils";

import { Popover } from "./Popover";

interface ListBoxProps<T> extends AriaListBoxOptions<T> {
  state: SelectState<T>;
}
function ListBox<T>(props: ListBoxProps<T>) {
  const ref = useRef(null);
  const { state } = props;
  const { listBoxProps } = useListBox(props, state, ref);

  return (
    <ul
      {...listBoxProps}
      className="flex max-h-52 min-w-24 flex-col gap-1 overflow-y-scroll rounded-md border-outline-decorative bg-background-secondary px-1 py-2 shadow-md"
    >
      {[...state.collection].map((item) => (
        <Option item={item} key={item.key} state={state} />
      ))}
    </ul>
  );
}

interface OptionProps<T> {
  state: SelectState<T>;
  item: Node<T>;
}
function Option<T>({ item, state }: OptionProps<T>) {
  const ref = useRef(null);
  const { optionProps, isSelected } = useOption({ key: item.key }, state, ref);

  return (
    <li
      {...optionProps}
      className={cn(
        "font-label-medium flex w-full cursor-pointer items-center justify-between gap-6 rounded-sm px-3xs py-3xs text-label-medium font-normal text-text-primary outline-none hover:bg-interactive-secondary-button-hover-background",
        isSelected ? "bg-interactive-secondary-button-focus-background" : "",
      )}
      ref={ref}
    >
      {item.rendered}
    </li>
  );
}

export function Select<T extends object>(props: AriaSelectProps<T>) {
  const state = useSelectState(props);

  const ref = useRef(null);
  const { labelProps, triggerProps, valueProps, menuProps } = useSelect(props, state, ref);

  const { buttonProps } = useButton(triggerProps, ref);

  return (
    <div className="inline-block">
      <div {...labelProps}>{props.label}</div>
      <HiddenSelect
        isDisabled={props.isDisabled}
        label={props.label}
        name={props.name}
        state={state}
        triggerRef={ref}
      />
      <button {...buttonProps} className="flex items-center justify-between gap-2" ref={ref}>
        <div
          className="text-label-medium font-semibold text-interactive-tertiary-button-enabled-foreground"
          {...valueProps}
        >
          {state.selectedItem ? state.selectedItem.rendered : props.placeholder}
        </div>
        <CaretRightIcon className="rotate-90" />
      </button>
      {state.isOpen && (
        <Popover placement="bottom" state={state} triggerRef={ref}>
          <ListBox {...menuProps} state={state} />
        </Popover>
      )}
    </div>
  );
}
